// src/components/pages/myAGT/ProfessionalServices.js
import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  IconButton,
  FormControlLabel,
  Switch,
  Divider,
  InputAdornment,
  Tooltip,
  Alert
} from '@mui/material';
import {
  Plus,
  Trash2,
  DollarSign,
  Tag
} from 'lucide-react';
import { styled } from '@mui/material/styles';

// Color principal
const appleGreen = '#34c759';

// Componentes estilizados
const ServicePaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: '12px',
  border: '1px solid rgba(0,0,0,0.08)',
  position: 'relative',
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  }
}));

// Toggle estilo iOS mejorado
const IOSSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 48,
  height: 30,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(18px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: appleGreen,
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: appleGreen,
      border: `6px solid #fff`,
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.grey[100],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 26,
    height: 26,
    backgroundColor: '#fff',
    boxShadow: '0 2px 6px 0 rgba(0, 0, 0, 0.2)',
    transition: theme.transitions.create(['transform'], {
      duration: 300,
    }),
  },
  '& .MuiSwitch-track': {
    borderRadius: 30 / 2,
    backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 300,
    }),
  },
}));

// Toggle pequeño para modalidades
const IOSSwitchSmall = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: appleGreen,
        opacity: 1,
        border: 0,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: appleGreen,
      border: `5px solid #fff`,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
    backgroundColor: '#fff',
    boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.2)',
    transition: theme.transitions.create(['transform'], {
      duration: 300,
    }),
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 300,
    }),
  },
}));

const ProfessionalServices = ({ services, onChange, isEditMode }) => {
  const [newService, setNewService] = useState({
    name: '',
    price: '',
    description: '',
    isActive: true,
    supportedModes: {
      inPerson: true,
      homeVisit: false,
      online: false
    }
  });

  // Manejar cambios en un servicio existente
  const handleServiceChange = (index, field, value) => {
    const updatedServices = [...services];
    
    if (field === 'supportedModes') {
      updatedServices[index].supportedModes = {
        ...updatedServices[index].supportedModes,
        ...value
      };
    } else {
      updatedServices[index][field] = value;
    }
    
    onChange(updatedServices);
  };

  // Añadir nuevo servicio
  const handleAddService = () => {
    if (!newService.name || !newService.price) return;
    
    const serviceToAdd = {
      ...newService,
      id: Date.now().toString(), // ID temporal
      price: parseFloat(newService.price)
    };
    
    onChange([...services, serviceToAdd]);
    
    // Resetear formulario
    setNewService({
      name: '',
      price: '',
      description: '',
      isActive: true,
      supportedModes: {
        inPerson: true,
        homeVisit: false,
        online: false
      }
    });
  };

  // Eliminar servicio
  const handleRemoveService = (index) => {
    const updatedServices = [...services];
    updatedServices.splice(index, 1);
    onChange(updatedServices);
  };

  return (
    <Box>
      {services.length === 0 && !isEditMode ? (
        <Alert severity="info" sx={{ borderRadius: 2, mb: 2 }}>
          No has añadido servicios profesionales. Edita tu perfil para añadir servicios.
        </Alert>
      ) : (
        <Box sx={{ mb: 3 }}>
          {services.map((service, index) => (
            <ServicePaper key={service.id || index}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {isEditMode ? (
                      <TextField
                        fullWidth
                        placeholder="Nombre del servicio"
                        value={service.name}
                        onChange={(e) => handleServiceChange(index, 'name', e.target.value)}
                        variant="outlined"
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Tag size={16} />
                            </InputAdornment>
                          )
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                            backgroundColor: '#f5f5f7',
                            border: 'none',
                            '& fieldset': { border: 'none' },
                          }
                        }}
                      />
                    ) : (
                      service.name
                    )}
                  </Typography>
                  
                  {isEditMode ? (
                    <TextField
                      fullWidth
                      placeholder="Descripción (opcional)"
                      value={service.description || ''}
                      onChange={(e) => handleServiceChange(index, 'description', e.target.value)}
                      variant="outlined"
                      size="small"
                      multiline
                      rows={2}
                      sx={{
                        mt: 1,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '10px',
                          backgroundColor: '#f5f5f7',
                          border: 'none',
                          '& fieldset': { border: 'none' },
                        }
                      }}
                    />
                  ) : service.description ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {service.description}
                    </Typography>
                  ) : null}
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', height: '100%' }}>
                    {isEditMode ? (
                      <TextField
                        placeholder="Precio"
                        value={service.price}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          handleServiceChange(index, 'price', value);
                        }}
                        variant="outlined"
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <DollarSign size={16} />
                            </InputAdornment>
                          )
                        }}
                        sx={{
                          width: '120px',
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                            backgroundColor: '#f5f5f7',
                            border: 'none',
                            '& fieldset': { border: 'none' },
                          }
                        }}
                      />
                    ) : (
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 600,
                          color: appleGreen
                        }}
                      >
                        ${service.price}
                      </Typography>
                    )}
                    
                    <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {service.isActive ? 'Activo' : 'Inactivo'}
                      </Typography>
                      
                      <FormControlLabel
                        control={
                          <IOSSwitch
                            checked={service.isActive}
                            onChange={(e) => handleServiceChange(index, 'isActive', e.target.checked)}
                            disabled={!isEditMode}
                          />
                        }
                        label=""
                      />
                      
                      {isEditMode && (
                        <IconButton 
                          onClick={() => handleRemoveService(index)}
                          size="small"
                          color="error"
                          sx={{ ml: 1 }}
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </Grid>
                
                {isEditMode && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                      Modalidades disponibles:
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <FormControlLabel
                        control={
                          <IOSSwitchSmall
                            checked={service.supportedModes?.inPerson || false}
                            onChange={(e) => handleServiceChange(
                              index, 
                              'supportedModes', 
                              { inPerson: e.target.checked }
                            )}
                          />
                        }
                        label="Presencial"
                      />
                      
                      <FormControlLabel
                        control={
                          <IOSSwitchSmall
                            checked={service.supportedModes?.homeVisit || false}
                            onChange={(e) => handleServiceChange(
                              index, 
                              'supportedModes', 
                              { homeVisit: e.target.checked }
                            )}
                          />
                        }
                        label="A domicilio"
                      />
                      
                      <FormControlLabel
                        control={
                          <IOSSwitchSmall
                            checked={service.supportedModes?.online || false}
                            onChange={(e) => handleServiceChange(
                              index, 
                              'supportedModes', 
                              { online: e.target.checked }
                            )}
                          />
                        }
                        label="Online"
                      />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </ServicePaper>
          ))}
        </Box>
      )}
      
      {isEditMode && (
        <Box sx={{ 
          mt: 2, 
          p: 2, 
          border: '1px dashed rgba(0,0,0,0.2)', 
          borderRadius: '12px',
          backgroundColor: 'rgba(0,0,0,0.02)'
        }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Añadir nuevo servicio
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre del servicio"
                placeholder="Ej: Consulta general"
                value={newService.name}
                onChange={(e) => setNewService({...newService, name: e.target.value})}
                variant="outlined"
                size="small"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Precio"
                placeholder="Ej: 30000"
                value={newService.price}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  setNewService({...newService, price: value});
                }}
                variant="outlined"
                size="small"
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DollarSign size={16} />
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción (opcional)"
                placeholder="Describe brevemente este servicio"
                value={newService.description}
                onChange={(e) => setNewService({...newService, description: e.target.value})}
                variant="outlined"
                size="small"
                multiline
                rows={2}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                Modalidades disponibles:
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <FormControlLabel
                  control={
                    <IOSSwitchSmall
                      checked={newService.supportedModes.inPerson}
                      onChange={(e) => setNewService({
                        ...newService, 
                        supportedModes: {
                          ...newService.supportedModes,
                          inPerson: e.target.checked
                        }
                      })}
                    />
                  }
                  label="Presencial"
                />
                
                <FormControlLabel
                  control={
                    <IOSSwitchSmall
                      checked={newService.supportedModes.homeVisit}
                      onChange={(e) => setNewService({
                        ...newService, 
                        supportedModes: {
                          ...newService.supportedModes,
                          homeVisit: e.target.checked
                        }
                      })}
                    />
                  }
                  label="A domicilio"
                />
                
                <FormControlLabel
                  control={
                    <IOSSwitchSmall
                      checked={newService.supportedModes.online}
                      onChange={(e) => setNewService({
                        ...newService, 
                        supportedModes: {
                          ...newService.supportedModes,
                          online: e.target.checked
                        }
                      })}
                    />
                  }
                  label="Online"
                />
              </Box>
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<Plus size={16} />}
              onClick={handleAddService}
              disabled={!newService.name || !newService.price}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                backgroundColor: appleGreen,
                '&:hover': {
                  backgroundColor: '#2aab4c',
                },
                boxShadow: 'none',
              }}
            >
              Añadir servicio
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ProfessionalServices;